<template>
  <div class="cordyceps-zoom">
    <div class="scale-wrapper" ref="containerRef">
      <!-- 背景图 -->
      <div
        class="main-bg"
        :style="{ backgroundImage: `url(${mainImageUrl})` }"
      >
        <!-- 细节点 -->
        <div
          v-for="(thumb, index) in thumbnailList"
          :key="index"
          class="detail-spot"
          :style="getSpotStyle(thumb)"
          @mouseenter="activeZoom = thumb.partKey"
          @mouseleave="activeZoom = ''"
        >
          <img
            :src="zoomImages[thumb.partKey]"
            class="detail-img"
            :class="{ active: activeZoom === thumb.partKey }"
            :alt="thumb.title || `细节${index + 1}`"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";

// === 带默认数据 ===
const props = defineProps({
  mainImageUrl: {
    type: String,
    default: "https://youke1.picui.cn/s1/2025/08/26/68adc37f9283d.png",
  },
  thumbnailList: {
    type: Array,
    default: () => [
      { partKey: "part1", title: "左上细节", x: 0.33, y: 0.34, size: 350 },
      { partKey: "part2", title: "右上细节", x: 0.74, y: 0.35, size: 310 },
      { partKey: "part3", title: "左下细节", x: 0.30, y: 0.70, size: 310 },
      { partKey: "part4", title: "右下细节", x: 0.66, y: 0.75, size: 320 },
    ],
  },
  zoomImages: {
    type: Object,
    default: () => ({
      part1: "https://youke1.picui.cn/s1/2025/08/27/68af2522e7078.png",
      part2: "https://youke1.picui.cn/s1/2025/08/27/68af2522a001d.png",
      part3: "https://youke1.picui.cn/s1/2025/08/27/68af252341b59.png",
      part4: "https://youke1.picui.cn/s1/2025/08/27/68af252342480.png",
    }),
  }
});

const activeZoom = ref("");
const containerRef = ref(null);
const containerSize = ref({ width: 0, height: 0 });
const imageSize = ref({ width: 0, height: 0 });

const updateSize = () => {
  if (containerRef.value) {
    const rect = containerRef.value.getBoundingClientRect();
    containerSize.value = { width: rect.width, height: rect.height };
  }
};

// 预加载图片，拿到原始宽高
const loadImage = (src) => {
  const img = new Image();
  img.src = src;
  img.onload = () => {
    imageSize.value = { width: img.width, height: img.height };
    updateSize();
  };
};

// 计算坐标（考虑 cover 裁切）
const getSpotStyle = (thumb) => {
  const { width: cw, height: ch } = containerSize.value;
  const { width: iw, height: ih } = imageSize.value;
  if (!cw || !ch || !iw || !ih) return {};

  // cover 缩放比
  const scale = Math.max(cw / iw, ch / ih);
  const displayWidth = iw * scale;
  const displayHeight = ih * scale;

  // 偏移量（被裁切的部分）
  const offsetX = (cw - displayWidth) / 2;
  const offsetY = (ch - displayHeight) / 2;

  // 按比例计算真实位置
  const px = thumb.x * iw * scale + offsetX;
  const py = thumb.y * ih * scale + offsetY;

  // 点大小（按宽度缩放）
  const baseSize = Math.min(cw, 1200);
  const size = thumb.size * (baseSize / 1200);

  return {
    position: "absolute",
    left: `${px}px`,
    top: `${py}px`,
    width: `${size}px`,
    height: `${size}px`,
    transform: "translate(-50%, -50%)"
  };
};

onMounted(() => {
  loadImage(props.mainImageUrl);
  window.addEventListener("resize", updateSize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateSize);
});
</script>

<style scoped>
.scale-wrapper {
  position: relative;
  width: 100%;
  height: 100vh; /* 占满屏幕 */
  overflow: hidden;
}

.main-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.detail-spot {
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.3);
}

.detail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.detail-img.active {
  transform: scale(2);
}
</style>
